import React, { useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Collapse,
  Badge,
  Card,
  CardContent,
  Box,
  Typography
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptIcon from '@mui/icons-material/Receipt';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { Tooltip, Chip } from '@mui/material';

import {
  recupereListeDemandes,
  recupereInfosClient,
  majCom,
  majComVisa,
  recupereCommentairesVisa,
  traitementAssurance,
  supprimerComVisaEtPasserATraiter,
  archiverDemande,
  desarchiverDemande,
  recupererAlertes,
  creerAlerte,
  supprimerAlerte,
  AlerteDossier,
  creerDossierManuel,
  majEtatVisa,
  majEtatPaiement
} from '../Services';
import { getApiUrl } from '../api/config';
import NouveauDossierDialog from './NouveauDossierDialog';
import AdresseFacturationDossierDialog from './AdresseFacturationDossierDialog';
import { Demandes } from '../models/Demandes';
import DemandeActions from './DemandeActions';
import InsuranceIndicator from './InsuranceIndicator';
import EtatVisaSelect from './EtatVisaSelect';
import EtatPaiementSelect from './EtatPaiementSelect';
import { usePersistentState } from '../hooks/usePersistentState';
import useIsMobile from '../hooks/useIsMobile';
import { adresseClient } from '../services/adresseClient';

// Puce « VIP » : service VIP payé (option TraitementVIP), sinon invisible pour un eVisa (bouton Options masqué)
const estVip = (d: Demandes) => (d.options || '').includes('TraitementVIP');
const PUCE_VIP_SX = { ml: 0.5, height: '18px', fontSize: '10px', fontWeight: 'bold', backgroundColor: '#1B1D1F', color: '#FFFFFF' };

/**
 * Formate la date/heure actuelle en JJ/MM/YYYY HH:MM
 */
const getFormattedDate = () =>
  new Date()
    .toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
    .replace(',', '');

/**
 * Tente de convertir « JJ/MM/YYYY » en Date ; renvoie null si échec
 */
const parseFrDateString = (s: string): Date | null => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s.trim());
  if (!m) return null;
  const [, day, month, year] = m;
  return new Date(Number(year), Number(month) - 1, Number(day));
};

/**
 * Formate n'importe quel type de date en JJ/MM/YYYY
 */
const formatDate = (input: string | Date): string => {
  if (input instanceof Date) {
    return input.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }
  // D'abord essayer le format JJ/MM/YYYY (format français)
  const fr = parseFrDateString(input);
  if (fr) {
    return formatDate(fr);
  }
  // Ensuite essayer ISO seulement si ce n'est pas au format JJ/MM/YYYY
  // pour éviter que "02/11/2025" soit interprété comme MM/DD/YYYY
  if (!input.includes('/')) {
    const iso = new Date(input);
    if (!isNaN(iso.getTime())) {
      return formatDate(iso);
    }
  }
  // Inconnu, retourner tel quel
  return input;
};

export const ListeDemandes: React.FC<{
  onSelectionDemande: (idVoyageur: number, chemin: string, pays: string, idDemande?: number, email?: string, source?: string) => void;
  onCollapsedChange?: (isCollapsed: boolean) => void;
  onFacturationClick?: (demandeId: number) => void;
}> = ({ onSelectionDemande, onCollapsedChange, onFacturationClick }) => {
  // États persistants
  const [visaFilter, setVisaFilter] = usePersistentState<'all' | 'visa' | 'evisa'>(
    'ld_visaFilter',
    'all'
  );
  const [etatFilter, setEtatFilter] = usePersistentState<string>(
    'ld_etatFilter',
    ''
  );
  const [searchTerm, setSearchTerm] = usePersistentState<string>(
    'ld_searchTerm',
    ''
  );
  const [selectedId, setSelectedId] = usePersistentState<number | null>(
    'ld_selectedId',
    null
  );
  const [showArchived, setShowArchived] = usePersistentState<boolean>(
    'ld_showArchived',
    false
  );

  // États volatiles
  const [demandes, setDemandes] = React.useState<Demandes[]>([]);
  const [filteredDemandes, setFilteredDemandes] = React.useState<Demandes[]>([]);
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [selectedEmail, setSelectedEmail] = React.useState<string | null>(null);
  const [filteredDemandeId, setFilteredDemandeId] = React.useState<number | null>(null);
  const [lastClickTime, setLastClickTime] = React.useState<number>(0);

  const [actionsOpen, setActionsOpen] = React.useState(false);

  const [allCommentaires, setAllCommentaires] = React.useState<string>('');
  const [commentaires, setCommentaires] = React.useState<string>('');
  const [commentairesVisa, setCommentairesVisa] = React.useState<string>('');
  const [allCommentairesVisa, setAllCommentairesVisa] = React.useState<string>('');

  // Alertes planifiées du dossier sélectionné
  const [alertes, setAlertes] = React.useState<AlerteDossier[]>([]);
  const [alerteDialogOpen, setAlerteDialogOpen] = React.useState(false);
  const [alerteDate, setAlerteDate] = React.useState('');
  const [alerteHeure, setAlerteHeure] = React.useState('');
  const [alerteMessage, setAlerteMessage] = React.useState('');

  // Création manuelle d'un dossier (NouveauDossierDialog)
  const [nouveauDialogOpen, setNouveauDialogOpen] = React.useState(false);
  // Adresse de facturation d'un dossier créé manuellement (id du dossier ouvert dans le dialog)
  const [adresseFacturationDe, setAdresseFacturationDe] = React.useState<number | null>(null);

  const [openCommentaires, setOpenCommentaires] = React.useState(true);
  const [openCommentairesVisa, setOpenCommentairesVisa] = React.useState(true);

  const [clientInfoOpen, setClientInfoOpen] = React.useState(false);
  const [clientInfo, setClientInfo] = React.useState<any>(null);

  const [insuranceDialogOpen, setInsuranceDialogOpen] = React.useState(false);
  const [insuranceId, setInsuranceId] = React.useState<number | null>(null);

  const [optionsDialogOpen, setOptionsDialogOpen] = React.useState(false);
  const [selectedOptions, setSelectedOptions] = React.useState<string[] | null>(
    null
  );

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Fonction pour récupérer les demandes
  const fetchDemandes = async () => {
    try {
      const result = await recupereListeDemandes(showArchived);
      setDemandes(result);
    } catch (error) {
      console.error('Erreur lors de la récupération des demandes:', error);
    }
  };

  // Récupération initiale des demandes
  useEffect(() => {
    fetchDemandes();
  }, [showArchived]);

  // Application des filtres et recherche
  useEffect(() => {
    let temp = [...demandes];

    // Exclure les demandes Legrand (emails contenant legrand.com ou legrand.fr)
    temp = temp.filter((d) => {
      const email = d.email?.toLowerCase() || '';
      return !email.includes('legrand.com') && !email.includes('legrand.fr');
    });

    // Si une demande est filtrée (double-clic), n'afficher que celle-ci
    if (filteredDemandeId !== null) {
      temp = temp.filter((d) => d.id === filteredDemandeId);
      setFilteredDemandes(temp);
      return;
    }

    // Filtre Visa / Evisa
    if (visaFilter === 'visa') {
      temp = temp.filter((d) => !d.titre.toLowerCase().includes('evisa'));
    } else if (visaFilter === 'evisa') {
      temp = temp.filter((d) => d.titre.toLowerCase().includes('evisa'));
    }

    // Filtre d'état
    switch (etatFilter) {
      case 'Formulaire complet':
        temp = temp.filter((d) => d.etat_visa === 'Formulaire complet');
        break;
      case 'Vérification éligibilité':
        temp = temp.filter((d) => d.etat_visa === 'Vérification éligibilité');
        break;
      case 'en attente réception dossier':
        temp = temp.filter((d) => {
          const etat = d.etat_visa?.toLowerCase() || '';
          return etat.includes('attente') && etat.includes('réception') ||
                 etat.includes('attente') && etat.includes('reception') ||
                 etat === 'en attente réception dossier' ||
                 etat === 'en attente reception dossier';
        });
        break;
      case 'Attente paiement':
        temp = temp.filter((d) => d.etat_paiement === 'attente paiement' || d.etat_paiement === 'Attente paiement' || d.etat_visa === 'Attente paiement');
        break;
      case 'À faire':
        temp = temp.filter((d) => {
          if (d.etat_visa !== 'à traiter') return false;
          // Les dossiers hors appli (EXT : créés manuellement en admin ou via un
          // canal externe) n'ont pas d'état de paiement suivi dans l'appli
          // (etat_paiement vide ou 'à facturer') : ne pas les écarter là-dessus,
          // sinon ils n'apparaissent jamais dans « À faire ».
          const horsApp = !!d.source && d.source !== 'app';
          return horsApp || ['payé', 'virement'].includes(d.etat_paiement);
        });
        break;
      case 'À vérifier':
        temp = temp.filter((d) => d.etat_visa === 'à vérifier' || d.etat_visa === 'Reçu');
        break;
      case 'Attente visa':
        temp = temp.filter((d) => d.etat_visa === 'en cours');
        break;
      case 'traité':
        temp = temp.filter((d) => d.etat_visa === 'traité');
        break;
      case 'Dossier en souffrance':
        temp = temp.filter((d) => d.etat_visa === 'Dossier en souffrance');
        break;
      case 'e-card':
        // Filtre pour les dossiers Inde eVisa ou Togo eVisa nécessitant une e-card urgente
        temp = temp.filter((d) => {
          // ========== INDE eVISA / eCARD ==========
          // Calculé par le backend (GET /demandes) avec les critères du mail de la règle e-cards Inde du menu
          // Alertes : arrivée en Inde du formulaire sinon départ, dans 4 jours ou moins, e-card non traitée
          if (d.ecard_urgente) return true;

          // ========== TOGO eVISA ==========
          const isTogoEVisa = d.titre.toLowerCase().includes('togo') && d.titre.toLowerCase().includes('evisa');

          if (isTogoEVisa) {
            // Vérifier que l'e-card n'est pas traitée
            const eCardNotProcessed = !d.etat_ecard || d.etat_ecard !== 'traité';
            if (!eCardNotProcessed) return false;

            // Vérifier dateSortie dans 5 jours ou moins
            if (!d.dateSortie) return false;

            const sortieDate = parseFrDateString(d.dateSortie);
            if (!sortieDate) return false;

            const today = new Date();
            today.setHours(0, 0, 0, 0);
            sortieDate.setHours(0, 0, 0, 0);

            const diffTime = sortieDate.getTime() - today.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            // Retourner true si la sortie est entre aujourd'hui et dans 5 jours
            return diffDays >= 0 && diffDays <= 5;
          }

          return false;
        });
        break;
      default:
        break;
    }

    // Recherche textuelle
    if (searchTerm.trim() !== '') {
      const lower = searchTerm.toLowerCase();
      temp = temp.filter((d) => {
        const dCrea = formatDate(d.date_creation);
        const dDep = formatDate(d.datedepart);
        return (
          d.id?.toString().includes(lower) ||
          d.nom.toLowerCase().includes(lower) ||
          d.prenom.toLowerCase().includes(lower) ||
          d.titre.toLowerCase().includes(lower) ||
          d.etat_visa.toLowerCase().includes(lower) ||
          dCrea.includes(lower) ||
          dDep.includes(lower)
        );
      });
    }

    setFilteredDemandes(temp);
  }, [demandes, visaFilter, etatFilter, searchTerm, filteredDemandeId]);

  // Sélection d'une ligne avec gestion du double-clic
  const handleRowClick = (
    id: number,
    idVoyageur: number,
    chemin: string,
    pays: string
  ) => {
    const currentTime = new Date().getTime();
    const timeDiff = currentTime - lastClickTime;

    // Détection du double-clic (moins de 300ms entre les clics sur la même ligne)
    if (timeDiff < 300 && selectedId === id) {
      // Double-clic : basculer le filtrage
      if (filteredDemandeId === id) {
        // Si déjà filtré sur cette demande, retirer le filtre
        setFilteredDemandeId(null);
        onCollapsedChange?.(false);
      } else {
        // Sinon, filtrer sur cette demande
        setFilteredDemandeId(id);
        onCollapsedChange?.(true);
      }
    } else {
      // Simple clic : sélection normale
      setSelectedId(id);
      setSelectedIds([id]);
      const d = demandes.find((x) => x.id === id);
      if (d) {
        setSelectedEmail(adresseClient(d.email, d.source));
        setAllCommentaires(d.commentaires || '');
        setAllCommentairesVisa(d.commentairesvisa || '');
        setCommentairesVisa('');
        loadAlertes(id);
      }
      onSelectionDemande(idVoyageur, chemin, pays, id, d ? adresseClient(d.email, d.source) : undefined, d ? d.source : undefined);
    }

    setLastClickTime(currentTime);
  };

  // Changement d'état d'un dossier directement depuis la liste (édition inline)
  const handleChangeEtat = async (d: Demandes, nouvelEtat: string) => {
    if (!d.id || nouvelEtat === d.etat_visa) return;
    const ancienEtat = d.etat_visa;
    // Mise à jour optimiste
    setDemandes((prev) =>
      prev.map((x) => (x.id === d.id ? { ...x, etat_visa: nouvelEtat } : x))
    );
    try {
      await majEtatVisa(d.id, nouvelEtat);
    } catch (error) {
      console.error("Erreur lors du changement d'état:", error);
      // Rollback en cas d'échec
      setDemandes((prev) =>
        prev.map((x) => (x.id === d.id ? { ...x, etat_visa: ancienEtat } : x))
      );
      window.alert("Erreur lors du changement d'état du dossier.");
    }
  };

  // Changement de l'état de paiement directement depuis la liste (édition inline), comme l'état
  const handleChangePaiement = async (d: Demandes, nouvelEtat: string) => {
    if (!d.id || nouvelEtat === d.etat_paiement) return;
    const ancienEtat = d.etat_paiement;
    // Mise à jour optimiste
    setDemandes((prev) =>
      prev.map((x) => (x.id === d.id ? { ...x, etat_paiement: nouvelEtat } : x))
    );
    try {
      await majEtatPaiement(d.id, nouvelEtat);
    } catch (error) {
      console.error("Erreur lors du changement d'état de paiement:", error);
      // Rollback en cas d'échec
      setDemandes((prev) =>
        prev.map((x) => (x.id === d.id ? { ...x, etat_paiement: ancienEtat } : x))
      );
      window.alert("Erreur lors du changement d'état de paiement du dossier.");
    }
  };

  // Recherche change
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Ouvre popup options
  const handleOpenOptionsDialog = (opts: string[]) => {
    setSelectedOptions(opts);
    setOptionsDialogOpen(true);
  };
  const handleCloseOptionsDialog = () => {
    setOptionsDialogOpen(false);
    setSelectedOptions(null);
  };

  // Fonctions d'archivage
  const handleArchiveDemande = async (id: number) => {
    try {
      await archiverDemande(id);
      await fetchDemandes();
    } catch (error) {
      console.error('Erreur lors de l\'archivage:', error);
    }
  };

  const handleDesarchiveDemande = async (id: number) => {
    try {
      await desarchiverDemande(id);
      await fetchDemandes();
    } catch (error) {
      console.error('Erreur lors du désarchivage:', error);
    }
  };

  // Client info
  const handleOpenClientInfo = async (email: string) => {
    try {
      const data = await recupereInfosClient(email);
      setClientInfo(data);
      setClientInfoOpen(true);
    } catch (error) {
      console.error('Erreur récupération infos client:', error);
    }
  };
  const handleCloseClientInfo = () => {
    setClientInfoOpen(false);
    setClientInfo(null);
  };

  // Assurance dialog
  const handleOpenInsuranceDialog = (id: number) => {
    setInsuranceId(id);
    setInsuranceDialogOpen(true);
  };
  const handleCloseInsuranceDialog = () => {
    setInsuranceDialogOpen(false);
    setInsuranceId(null);
  };
  const handleConfirmInsurance = async () => {
    if (insuranceId !== null) {
      const d = demandes.find((x) => x.id === insuranceId);
      if (d) {
        const newVal = d.assurancevisa === 'oui' ? 'traité' : 'oui';
        try {
          await traitementAssurance(insuranceId, newVal);
          await reloadDemandes(false);
        } catch (e) {
          console.error(e);
        }
      }
      setInsuranceDialogOpen(false);
      setInsuranceId(null);
    }
  };

  // Enregistrer commentaires généraux
  const handleSaveCommentaires = async () => {
    if (selectedId === null || commentaires.trim() === '') return;
    const line = `${getFormattedDate()} - ${commentaires}\n`;
    const updated = line + allCommentaires;
    setAllCommentaires(updated);
    setCommentaires('');
    const d = demandes.find((x) => x.id === selectedId);
    if (d) {
      try {
        await majCom(d.chemin, updated);
        await reloadDemandes(false);
      } catch (e) {
        console.error(e);
      }
    }
  };

  // Enregistrer commentaires visa (relus juste avant : la route remplace tout le texte, et l'extension Chrome a pu
  // ajouter une ligne depuis le chargement de la liste)
  const handleSaveCommentairesVisa = async () => {
    if (selectedId === null || commentairesVisa.trim() === '') return;
    const d = demandes.find((x) => x.id === selectedId);
    if (d) {
      try {
        const actuels = await recupereCommentairesVisa(selectedId);
        const updated = `${getFormattedDate()} - ${commentairesVisa}\n${actuels}`;
        await majComVisa(d.chemin, updated);
        setAllCommentairesVisa(updated);
        setCommentairesVisa('');
        await reloadDemandes(false);
      } catch (e) {
        console.error(e);
      }
    }
  };

  // Supprimer commentaires visa et passer à traiter
  const handleSupprimerComVisaEtPasserATraiter = async () => {
    if (selectedId === null) return;
    const d = demandes.find((x) => x.id === selectedId);
    if (d) {
      try {
        await supprimerComVisaEtPasserATraiter(d.chemin);
        setCommentairesVisa('');
        setAllCommentairesVisa('');
        await reloadDemandes(false);
      } catch (e) {
        console.error(e);
      }
    }
  };

  // ---------- Alertes planifiées ----------
  const loadAlertes = async (demandeId: number) => {
    try {
      const data = await recupererAlertes(demandeId);
      setAlertes(data);
    } catch (e) {
      console.error(e);
    }
  };

  const formatAlerteDate = (dt: string) => {
    const d = new Date(dt.replace(' ', 'T'));
    if (isNaN(d.getTime())) return dt;
    return d
      .toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
      .replace(',', '');
  };

  const handleCreerAlerte = async () => {
    if (selectedId === null || !alerteDate || !alerteHeure || !alerteMessage.trim()) return;
    const datetimeEnvoi = `${alerteDate} ${alerteHeure}:00`;
    try {
      await creerAlerte(selectedId, datetimeEnvoi, alerteMessage.trim());
      setAlerteDialogOpen(false);
      setAlerteDate('');
      setAlerteHeure('');
      setAlerteMessage('');
      await loadAlertes(selectedId);
    } catch (e) {
      console.error('Erreur création alerte', e);
    }
  };

  const handleSupprimerAlerte = async (alerteId: number) => {
    try {
      await supprimerAlerte(alerteId);
      if (selectedId !== null) await loadAlertes(selectedId);
    } catch (e) {
      console.error('Erreur suppression alerte', e);
    }
  };

  // Ouvrir / fermer actions
  const handleOpenActions = () => {
    if (selectedIds.length > 0) setActionsOpen(true);
  };
  const handleCloseActions = () => setActionsOpen(false);

  // Rechargement
  const reloadDemandes = async (clearFilters = true) => {
    try {
      const result = await recupereListeDemandes();
      setDemandes(result);
      setSelectedIds([]);
      setSelectedEmail(null);
      setSelectedId(null);
      if (clearFilters) {
        setVisaFilter('all');
        setEtatFilter('');
        setSearchTerm('');
      }
      setAllCommentaires('');
      setCommentaires('');
      setCommentairesVisa('');
      setAllCommentairesVisa('');
      setAlertes([]);
      tableContainerRef.current?.scrollTo({ top: 0 });
    } catch (error) {
      console.error('Erreur reload demandes:', error);
    }
  };

  const selectedDemandesData = filteredDemandes.filter((d) =>
    selectedIds.includes(d.id!)
  );

  // Fonctions pour obtenir les couleurs selon les \u00e9tats
  const getEtatColor = (etat: string) => {
    switch (etat.toLowerCase()) {
      case 'trait\u00e9':
        return '#4caf50';
      case 'en cours':
        return '#03a9f4';
      case '\u00e0 traiter':
        return '#ff9800';
      case 'v\u00e9rification \u00e9ligibilit\u00e9':
        return '#9c27b0';
      case 'formulaire complet':
      case 'complet':
        return '#673ab7';
      case 'en attente r\u00e9ception dossier':
        return '#e91e63';
      case 'attente visa':
        return '#03a9f4';
      default:
        return '#757575';
    }
  };

  const getPaiementColor = (etat: string) => {
    switch (etat.toLowerCase()) {
      case 'pay\u00e9':
        return '#4caf50';
      case 'virement':
        return '#2196f3';
      case 'attente paiement':
        return '#ff5722';
      case 'attente facturation':
        return '#ff9800';
      case 'à facturer':
        return '#009688';
      case 'rembours\u00e9':
        return '#9c27b0';
      case 'annul\u00e9':
        return '#f44336';
      default:
        return '#757575';
    }
  };

  return (
    <>
      {/* Barre de recherche et actions */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={5}>
          <TextField
            label="Rechercher"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearch}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleOpenActions}
            disabled={selectedIds.length === 0}
          >
            Actions
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              setVisaFilter('all');
              setEtatFilter('');
              setSearchTerm('');
              fetchDemandes();
            }}
          >
            Rafraîchir
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Button
            fullWidth
            variant={showArchived ? "contained" : "outlined"}
            color="primary"
            startIcon={<ArchiveIcon />}
            onClick={() => setShowArchived(!showArchived)}
          >
            {showArchived ? 'Masquer Archives' : 'Afficher Archives'}
          </Button>
        </Grid>
      </Grid>

      {/* Filtres Visa/eVisa */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item>
          <Button
            variant={visaFilter === 'all' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setVisaFilter('all')}
          >
            Tous
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant={visaFilter === 'visa' ? 'contained' : 'outlined'}
            size="small"
            color="primary"
            onClick={() => setVisaFilter('visa')}
          >
            Visa
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant={visaFilter === 'evisa' ? 'contained' : 'outlined'}
            size="small"
            color="secondary"
            onClick={() => setVisaFilter('evisa')}
          >
            eVisa
          </Button>
        </Grid>
        <Grid item sx={{ ml: 'auto' }}>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setNouveauDialogOpen(true)}
          >
            Nouveau dossier
          </Button>
        </Grid>
      </Grid>

      {/* Filtres d'état */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item>
          <Button
            variant={etatFilter === '' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setEtatFilter('')}
          >
            Tous
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant={etatFilter === 'Formulaire complet' ? 'contained' : 'outlined'}
            size="small"
            sx={{
              bgcolor: etatFilter === 'Formulaire complet' ? '#673ab7' : undefined,
              color: etatFilter === 'Formulaire complet' ? 'white' : '#673ab7',
              '&:hover': { bgcolor: '#5e35b1' }
            }}
            onClick={() => setEtatFilter('Formulaire complet')}
          >
            Complet
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant={etatFilter === 'Vérification éligibilité' ? 'contained' : 'outlined'}
            size="small"
            sx={{
              bgcolor: etatFilter === 'Vérification éligibilité' ? '#9c27b0' : undefined,
              color: etatFilter === 'Vérification éligibilité' ? 'white' : '#9c27b0',
              '&:hover': { bgcolor: '#7b1fa2' }
            }}
            onClick={() => setEtatFilter('Vérification éligibilité')}
          >
            Vérification éligibilité
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant={etatFilter === 'en attente réception dossier' ? 'contained' : 'outlined'}
            size="small"
            sx={{
              bgcolor: etatFilter === 'en attente réception dossier' ? '#e91e63' : undefined,
              color: etatFilter === 'en attente réception dossier' ? 'white' : '#e91e63',
              '&:hover': { bgcolor: '#d81b60' }
            }}
            onClick={() => setEtatFilter('en attente réception dossier')}
          >
            En attente réception dossier
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant={etatFilter === 'Attente paiement' ? 'contained' : 'outlined'}
            size="small"
            sx={{
              bgcolor: etatFilter === 'Attente paiement' ? '#ff5722' : undefined,
              color: etatFilter === 'Attente paiement' ? 'white' : '#ff5722',
              '&:hover': { bgcolor: '#f4511e' }
            }}
            onClick={() => setEtatFilter('Attente paiement')}
          >
            Attente paiement
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant={etatFilter === 'À faire' ? 'contained' : 'outlined'}
            size="small"
            sx={{
              bgcolor: etatFilter === 'À faire' ? '#ff9800' : undefined,
              color: etatFilter === 'À faire' ? 'white' : '#ff9800',
              '&:hover': { bgcolor: '#f57c00' }
            }}
            onClick={() => setEtatFilter('À faire')}
          >
            À faire
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant={etatFilter === 'À vérifier' ? 'contained' : 'outlined'}
            size="small"
            sx={{
              bgcolor: etatFilter === 'À vérifier' ? '#9c27b0' : undefined,
              color: etatFilter === 'À vérifier' ? 'white' : '#9c27b0',
              '&:hover': { bgcolor: '#7b1fa2' }
            }}
            onClick={() => setEtatFilter('À vérifier')}
          >
            À vérifier
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant={etatFilter === 'Attente visa' ? 'contained' : 'outlined'}
            size="small"
            sx={{
              bgcolor: etatFilter === 'Attente visa' ? '#03a9f4' : undefined,
              color: etatFilter === 'Attente visa' ? 'white' : '#03a9f4',
              '&:hover': { bgcolor: '#039be5' }
            }}
            onClick={() => setEtatFilter('Attente visa')}
          >
            Attente visa
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant={etatFilter === 'Dossier en souffrance' ? 'contained' : 'outlined'}
            size="small"
            sx={{
              bgcolor: etatFilter === 'Dossier en souffrance' ? '#ff9800' : undefined,
              color: etatFilter === 'Dossier en souffrance' ? 'white' : '#ff9800',
              '&:hover': { bgcolor: '#f57c00' }
            }}
            onClick={() => setEtatFilter('Dossier en souffrance')}
          >
            Dossier en souffrance
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant={etatFilter === 'traité' ? 'contained' : 'outlined'}
            size="small"
            sx={{
              bgcolor: etatFilter === 'traité' ? '#4caf50' : undefined,
              color: etatFilter === 'traité' ? 'white' : '#4caf50',
              '&:hover': { bgcolor: '#45a049' }
            }}
            onClick={() => setEtatFilter('traité')}
          >
            Traités
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant={etatFilter === 'e-card' ? 'contained' : 'outlined'}
            size="small"
            sx={{
              bgcolor: etatFilter === 'e-card' ? '#ff6b35' : undefined,
              color: etatFilter === 'e-card' ? 'white' : '#ff6b35',
              '&:hover': { bgcolor: '#e55a2b' },
              fontWeight: etatFilter === 'e-card' ? 'bold' : 'normal'
            }}
            onClick={async () => {
              setEtatFilter('e-card');
              // Appeler l'API pour marquer les e-cards urgentes comme "A traiter"
              try {
                const response = await fetch(getApiUrl('/ecards-urgentes/marquer-a-traiter'), {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json'
                  }
                });
                if (response.ok) {
                  const data = await response.json();
                  console.log(data.message);
                  // Rafraîchir la liste après la mise à jour
                  window.location.reload();
                }
              } catch (error) {
                console.error('Erreur lors de la mise à jour des e-cards urgentes:', error);
              }
            }}
          >
            E-card urgentes
          </Button>
        </Grid>
      </Grid>

      {!isMobile && (
      <TableContainer
        ref={tableContainerRef}
        component={Paper}
        sx={{
          maxHeight: 380,  // Hauteur pour afficher exactement 10 lignes + header
          overflow: 'auto',
          '& .MuiTableHead-root': {
            '& .MuiTableCell-root': {
              backgroundColor: '#f5f5f5',
              fontWeight: 'bold'
            }
          }
        }}
      >
        <Table stickyHeader size="small" aria-label="table des demandes">
          <TableHead>
            <TableRow>
              <TableCell sx={{ padding: '6px 8px', width: '40px' }} />
              <TableCell sx={{ padding: '6px 8px' }}>Nº</TableCell>
              <TableCell sx={{ padding: '6px 8px' }}>GC</TableCell>
              <TableCell sx={{ padding: '6px 8px' }}>Date demande</TableCell>
              <TableCell sx={{ padding: '6px 8px' }}>Pays</TableCell>
              <TableCell sx={{ padding: '6px 8px' }}>Validité</TableCell>
              <TableCell sx={{ padding: '6px 8px' }}>Nom</TableCell>
              <TableCell sx={{ padding: '6px 8px' }}>Prénom</TableCell>
              <TableCell sx={{ padding: '6px 8px' }}>Départ</TableCell>
              <TableCell sx={{ padding: '6px 8px' }}>Paiement</TableCell>
              <TableCell sx={{ padding: '6px 8px' }}>État</TableCell>
              <TableCell sx={{ padding: '6px 8px' }}>E-card</TableCell>
              <TableCell sx={{ padding: '6px 8px' }}>Options</TableCell>
              <TableCell sx={{ padding: '6px 8px' }}>Message</TableCell>
              <TableCell sx={{ padding: '6px 8px' }}>Client/Assurance</TableCell>
              <TableCell sx={{ padding: '6px 8px' }}>Facturation</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDemandes.map((d) => {
              const opts = d.options ? d.options.split(';').filter(Boolean) : [];
              return (
                <TableRow
                  key={d.id}
                  onClick={() =>
                    handleRowClick(d.id!, d.id_voyageur, d.chemin, d.titre)
                  }
                  hover
                  sx={{
                    backgroundColor: d.archived
                      ? '#f5f5f5'
                      : (selectedId === d.id ? '#e3f2fd' : 'inherit'),
                    opacity: d.archived ? 0.7 : 1,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: d.archived ? '#f5f5f5' : (selectedId === d.id ? '#bbdefb' : '#f5f5f5')
                    }
                  }}
                  title="Double-cliquez pour filtrer/défiltrer cette ligne"
                >
                  <TableCell sx={{ padding: '4px 8px', width: '40px' }}>
                    {d.archived ? (
                      <Tooltip title="Désarchiver">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDesarchiveDemande(d.id!);
                          }}
                        >
                          <UnarchiveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Archiver">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArchiveDemande(d.id!);
                          }}
                        >
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ padding: '4px 8px' }}>
                    {d.id}
                    {d.source && d.source !== 'app' && (
                      <Chip
                        label={d.source === 'externe_inde_tourisme' ? 'Inde T' : d.source === 'externe_inde_affaire' ? 'Inde B' : 'EXT'}
                        size="small"
                        sx={{
                          ml: 0.5,
                          height: '18px',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          backgroundColor: d.source === 'externe_inde_tourisme' ? '#e0f2f1' : '#fff3e0',
                          color: d.source === 'externe_inde_tourisme' ? '#00695c' : '#e65100',
                        }}
                      />
                    )}
                    {estVip(d) && <Chip label="VIP" size="small" title="Service VIP" sx={PUCE_VIP_SX} />}
                  </TableCell>
                  <TableCell sx={{ padding: '4px 8px' }}>{d.nomsociete}</TableCell>
                  <TableCell sx={{ padding: '4px 8px' }}>{formatDate(d.date_creation)}</TableCell>
                  <TableCell sx={{ padding: '4px 8px' }}>{d.titre}</TableCell>
                  <TableCell sx={{ padding: '4px 8px' }}>
                    {d.validite_visa && d.validite_visa.length > 20 ? (
                      <Tooltip title={d.validite_visa} arrow>
                        <span style={{
                          display: 'inline-block',
                          maxWidth: '120px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {d.validite_visa}
                        </span>
                      </Tooltip>
                    ) : (
                      <span>{d.validite_visa || '-'}</span>
                    )}
                  </TableCell>
                  <TableCell sx={{ padding: '4px 8px' }}>{d.nom}</TableCell>
                  <TableCell sx={{ padding: '4px 8px' }}>{d.prenom}</TableCell>
                  <TableCell sx={{ padding: '4px 8px' }}>{formatDate(d.datedepart)}</TableCell>
                  <TableCell sx={{ padding: '4px 8px' }}>
                    <EtatPaiementSelect
                      value={d.etat_paiement}
                      getColor={getPaiementColor}
                      onChange={(nouvelEtat) => handleChangePaiement(d, nouvelEtat)}
                    />
                  </TableCell>
                  <TableCell sx={{ padding: '4px 8px' }}>
                    <EtatVisaSelect
                      value={d.etat_visa}
                      titre={d.titre}
                      getColor={getEtatColor}
                      onChange={(nouvelEtat) => handleChangeEtat(d, nouvelEtat)}
                    />
                  </TableCell>
                  <TableCell sx={{ padding: '4px 8px' }}>
                    {((d.titre.toLowerCase().includes('inde') && d.titre.toLowerCase().includes('evisa')) ||
                      (d.titre.toLowerCase().includes('togo') && d.titre.toLowerCase().includes('evisa')) ||
                      (d.titre.toLowerCase().includes('inde') && d.titre.toLowerCase().includes('ecard')) ||
                      d.titre.toLowerCase().includes('chine')) &&
                     d.etat_ecard && (d.etat_ecard === 'traité' || d.etat_ecard === 'A traiter') && (
                      <span style={{
                        color: d.etat_ecard === 'traité' ? '#4caf50' : '#ff5722',
                        fontWeight: 'bold'
                      }}>
                        {d.etat_ecard === 'traité' ? 'Traité' : 'A traiter'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell sx={{ padding: '4px 8px' }}>
                    {!d.titre.toLowerCase().includes('evisa') && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenOptionsDialog(opts);
                        }}
                      >
                        <Badge
                          badgeContent={opts.length}
                          color={opts.length > 0 ? 'primary' : 'default'}
                        >
                          <InfoIcon fontSize="small" />
                        </Badge>
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell sx={{ padding: '4px 8px' }}>{d.infosdemande}</TableCell>
                  <TableCell sx={{ padding: '4px 8px' }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenClientInfo(d.email);
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <InsuranceIndicator
                      assurancevisa={d.assurancevisa}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenInsuranceDialog(d.id!);
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                    {d.source === 'admin_manuel' && (
                      <Tooltip title="Adresse de facturation">
                        <IconButton
                          size="small"
                          color={d.has_bdc ? 'default' : 'primary'}
                          onClick={(e) => {
                            e.stopPropagation();
                            setAdresseFacturationDe(d.id!);
                          }}
                        >
                          <HomeWorkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {Boolean(d.has_bdc) && (
                      <Tooltip title="Facturer">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            onFacturationClick?.(d.id!);
                          }}
                        >
                          <ReceiptIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Vue mobile : liste de cartes (remplace le tableau sous 600px) */}
      {isMobile && (
        <Box>
          {filteredDemandes.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Aucune demande
            </Typography>
          ) : (
            filteredDemandes.map((d) => {
              const opts = d.options ? d.options.split(';').filter(Boolean) : [];
              const showEcard =
                ((d.titre.toLowerCase().includes('inde') && d.titre.toLowerCase().includes('evisa')) ||
                  (d.titre.toLowerCase().includes('togo') && d.titre.toLowerCase().includes('evisa')) ||
                  (d.titre.toLowerCase().includes('inde') && d.titre.toLowerCase().includes('ecard')) ||
                  d.titre.toLowerCase().includes('chine')) &&
                d.etat_ecard && (d.etat_ecard === 'traité' || d.etat_ecard === 'A traiter');
              return (
                <Card
                  key={d.id}
                  onClick={() => handleRowClick(d.id!, d.id_voyageur, d.chemin, d.titre)}
                  sx={{
                    mb: 1,
                    cursor: 'pointer',
                    borderLeft: `5px solid ${getEtatColor(d.etat_visa)}`,
                    backgroundColor: selectedId === d.id ? '#e3f2fd' : (d.archived ? '#f5f5f5' : 'inherit'),
                    opacity: d.archived ? 0.7 : 1,
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        #{d.id} — {d.nom} {d.prenom}
                        {d.source && d.source !== 'app' && (
                          <Chip
                            label={d.source === 'externe_inde_tourisme' ? 'Inde T' : d.source === 'externe_inde_affaire' ? 'Inde B' : 'EXT'}
                            size="small"
                            sx={{
                              ml: 0.5,
                              height: '18px',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              backgroundColor: d.source === 'externe_inde_tourisme' ? '#e0f2f1' : '#fff3e0',
                              color: d.source === 'externe_inde_tourisme' ? '#00695c' : '#e65100',
                            }}
                          />
                        )}
                        {estVip(d) && <Chip label="VIP" size="small" title="Service VIP" sx={PUCE_VIP_SX} />}
                      </Typography>
                      <Box onClick={(e) => e.stopPropagation()}>
                        <EtatVisaSelect
                          value={d.etat_visa}
                          titre={d.titre}
                          getColor={getEtatColor}
                          onChange={(nouvelEtat) => handleChangeEtat(d, nouvelEtat)}
                          fontSize={13}
                        />
                      </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                      {d.titre}{d.validite_visa ? ` — ${d.validite_visa}` : ''}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                      <Typography variant="caption">
                        Demande : {formatDate(d.date_creation)} · Départ : {formatDate(d.datedepart)}
                      </Typography>
                      <Box onClick={(e) => e.stopPropagation()}>
                        <EtatPaiementSelect
                          value={d.etat_paiement}
                          getColor={getPaiementColor}
                          onChange={(nouvelEtat) => handleChangePaiement(d, nouvelEtat)}
                          fontSize={12}
                        />
                      </Box>
                    </Box>

                    {d.nomsociete && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        GC : {d.nomsociete}
                      </Typography>
                    )}

                    {showEcard && (
                      <Typography variant="caption" sx={{ display: 'block', color: d.etat_ecard === 'traité' ? '#4caf50' : '#ff5722', fontWeight: 'bold' }}>
                        E-card : {d.etat_ecard === 'traité' ? 'Traité' : 'A traiter'}
                      </Typography>
                    )}

                    {d.infosdemande && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                        {d.infosdemande}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                      {d.archived ? (
                        <Tooltip title="Désarchiver">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDesarchiveDemande(d.id!); }}>
                            <UnarchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Archiver">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleArchiveDemande(d.id!); }}>
                            <ArchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Infos client">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenClientInfo(d.email); }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <InsuranceIndicator
                        assurancevisa={d.assurancevisa}
                        onClick={(e) => { e.stopPropagation(); handleOpenInsuranceDialog(d.id!); }}
                      />
                      {!d.titre.toLowerCase().includes('evisa') && (
                        <Tooltip title="Options">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenOptionsDialog(opts); }}>
                            <Badge badgeContent={opts.length} color={opts.length > 0 ? 'primary' : 'default'}>
                              <InfoIcon fontSize="small" />
                            </Badge>
                          </IconButton>
                        </Tooltip>
                      )}
                      {d.source === 'admin_manuel' && (
                        <Tooltip title="Adresse de facturation">
                          <IconButton size="small" color={d.has_bdc ? 'default' : 'primary'} onClick={(e) => { e.stopPropagation(); setAdresseFacturationDe(d.id!); }}>
                            <HomeWorkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {Boolean(d.has_bdc) && (
                        <Tooltip title="Facturer">
                          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); onFacturationClick?.(d.id!); }}>
                            <ReceiptIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Box>
      )}

      {/* Dialog Assurance */}
      <Dialog open={insuranceDialogOpen} onClose={handleCloseInsuranceDialog}>
        <DialogTitle>Traitement de l'assurance</DialogTitle>
        <DialogContent>
          Êtes-vous sûr de vouloir changer l'état de l'assurance (oui ⇄ traité) ?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInsuranceDialog}>Annuler</Button>
          <Button onClick={handleConfirmInsurance} color="secondary">
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Options */}
      <Dialog open={optionsDialogOpen} onClose={handleCloseOptionsDialog}>
        <DialogTitle>Options</DialogTitle>
        <DialogContent>
          {selectedOptions && selectedOptions.length > 0 ? (
            selectedOptions.map((opt, i) => <div key={i}>{opt}</div>)
          ) : (
            <p>Aucune option</p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOptionsDialog}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Créer une alerte */}
      <Dialog
        open={alerteDialogOpen}
        onClose={() => setAlerteDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Créer une alerte</DialogTitle>
        <DialogContent>
          <TextField
            label="Date"
            type="date"
            fullWidth
            value={alerteDate}
            onChange={(e) => setAlerteDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={{ marginTop: 10 }}
          />
          <TextField
            label="Heure"
            type="time"
            fullWidth
            value={alerteHeure}
            onChange={(e) => setAlerteHeure(e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={{ marginTop: 16 }}
          />
          <TextField
            label="Message"
            multiline
            rows={3}
            fullWidth
            value={alerteMessage}
            onChange={(e) => setAlerteMessage(e.target.value)}
            style={{ marginTop: 16 }}
          />
          <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
            Le message sera envoyé par mail à contact@visamonde.com à la date et l'heure
            choisies.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlerteDialogOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleCreerAlerte}
            disabled={!alerteDate || !alerteHeure || !alerteMessage.trim()}
          >
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Adresse de facturation d'un dossier créé manuellement (crée ou met à jour son bon de commande) */}
      <AdresseFacturationDossierDialog
        open={adresseFacturationDe !== null}
        demandeId={adresseFacturationDe}
        onClose={() => setAdresseFacturationDe(null)}
        onEnregistre={() => reloadDemandes(false)}
      />

      {/* Nouveau dossier (création manuelle, formulaire à joindre facultatif, composeur du mail) */}
      <NouveauDossierDialog
        open={nouveauDialogOpen}
        onClose={() => setNouveauDialogOpen(false)}
        creerDossier={creerDossierManuel}
        onCree={() => reloadDemandes(false)}
      />

      {/* Commentaires */}
      <Grid container spacing={2} style={{ marginTop: 20 }}>
        <Grid item xs={12} sm={6}>
          <Button
            variant="text"
            startIcon={<ExpandMoreIcon />}
            onClick={() => setOpenCommentaires(!openCommentaires)}
          >
            {openCommentaires ? 'Masquer Commentaires' : 'Afficher Commentaires'}
          </Button>
          <Collapse in={openCommentaires}>
            <TextField
              label="Commentaires existants"
              variant="outlined"
              fullWidth
              multiline
              rows={5}
              value={allCommentaires}
              InputProps={{ readOnly: true }}
              style={{ marginTop: 10, maxHeight: 150, overflow: 'auto' }}
            />
            <TextField
              label="Nouveau Commentaire"
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              value={commentaires}
              onChange={(e) => setCommentaires(e.target.value)}
              style={{ marginTop: 15 }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveCommentaires}
              style={{ marginTop: 10 }}
            >
              Enregistrer
            </Button>
          </Collapse>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Button
            variant="text"
            startIcon={<ExpandMoreIcon />}
            onClick={() =>
              setOpenCommentairesVisa(!openCommentairesVisa)
            }
          >
            {openCommentairesVisa
              ? 'Masquer Commentaires Visa'
              : 'Afficher Commentaires Visa'}
          </Button>
          <Collapse in={openCommentairesVisa}>
            <TextField
              label="Commentaires visa existants"
              variant="outlined"
              fullWidth
              multiline
              rows={5}
              value={allCommentairesVisa}
              InputProps={{ readOnly: true }}
              style={{ marginTop: 10, maxHeight: 150, overflow: 'auto' }}
            />
            <TextField
              label="Nouveau commentaire visa"
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              value={commentairesVisa}
              onChange={(e) => setCommentairesVisa(e.target.value)}
              style={{ marginTop: 15 }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveCommentairesVisa}
              >
                Enregistrer et passer dossier à "en cours"
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={handleSupprimerComVisaEtPasserATraiter}
              >
                Supprimer com et passer dossier à traiter
              </Button>
            </div>

            {/* Alertes planifiées du dossier */}
            <div style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 16 }}>
              <Button
                variant="outlined"
                startIcon={<NotificationsActiveIcon />}
                onClick={() => setAlerteDialogOpen(true)}
              >
                Créer une alerte
              </Button>
              {alertes.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {alertes.map((al) => {
                    // Une alerte typée est un marqueur posé par un cron (ex. relance e-card) :
                    // son message porte l'information, et l'API refuse sa suppression.
                    const auto = !!al.type;
                    return (
                      <div
                        key={al.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          fontSize: 13,
                          color: al.statut === 'envoye' ? '#888' : '#333'
                        }}
                      >
                        <NotificationsActiveIcon
                          fontSize="small"
                          color={auto ? 'success' : al.statut === 'envoye' ? 'disabled' : 'warning'}
                        />
                        <span>
                          {formatAlerteDate(al.datetime_envoi)}
                          {auto
                            ? ` — ${al.message}`
                            : al.statut === 'envoye' ? ' — envoyée' : ''}
                        </span>
                        {!auto && (
                          <IconButton size="small" onClick={() => handleSupprimerAlerte(al.id)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Collapse>
        </Grid>
      </Grid>

      {/* Dialog Infos Client */}
      <Dialog
        open={clientInfoOpen}
        onClose={handleCloseClientInfo}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Informations du Client</DialogTitle>
        <DialogContent>
          {clientInfo ? (
            <>
              <p>Nom: {clientInfo.nom}</p>
              <p>Email: {clientInfo.email}</p>
              <p>Mot de passe: {clientInfo.mot_de_passe}</p>
              <p>Téléphone: {clientInfo.telephone}</p>
            </>
          ) : (
            <p>Chargement...</p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClientInfo}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Actions groupées  */}
      <DemandeActions
        open={actionsOpen}
        onValidated={() => reloadDemandes(true)}
        onClose={handleCloseActions}
        selectedIds={selectedIds}
        selectedEmail={selectedEmail}
        selectedDemandes={selectedDemandesData}
      />
    </>
  );
};